import React from "react";
import PlusCircleIcon from "@heroicons/react/24/outline/PlusCircleIcon";
import BellIcon from "@heroicons/react/24/outline/BellIcon";
import PencilIcon from "@heroicons/react/24/outline/PencilIcon";
import TrashIcon from "@heroicons/react/24/outline/TrashIcon";
import { format } from "date-fns";
import { type MyLatestReminderList } from "../types";
import { fakeUsers, matters as matterList } from "../../mocks/calendarMocks";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toDefaultDateFormat } from "@/helpers/dates";

export interface EventTableProps {
  eventsAndReminders: MyLatestReminderList["reminderList"];
  onAddSubtask: () => void;
  onSetReminder: () => void;
  onEditEvent: () => void;
  onDeleteEvent: () => void;
  onDateSelect?: (date: Date) => void;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const EventTable: React.FC<EventTableProps> = ({
  eventsAndReminders,
  onAddSubtask,
  onSetReminder,
  onEditEvent,
  onDeleteEvent,
}) => {
  const renderRow = (item: MyLatestReminderList["reminderList"][0]) => (
    <TableRow
      key={item.id}
      className={classNames(item.type === "event" ? "bg-gray-100 bg-opacity-50" : "bg-violet-100 bg-opacity-60")}
    >
      <TableCell className="px-4 py-4">
        {toDefaultDateFormat(item.datetime)}
        <br />
        <span className="text-sm text-indigo-500">{format(new Date(item.datetime), "EEEE")}</span>
        <br />
        <span className="text-xs text-gray-500">{item.location}</span>
      </TableCell>
      <TableCell className="w-1/3 px-4 py-4">
        <div className="text-md font-bold">{matterList.find(m => m.id === item.matterId)?.title}</div>
        {item.title}
      </TableCell>
      <TableCell className="px-4 py-4">{item.summary}</TableCell>
      <TableCell className="px-4 py-4">
        <div className="flex space-x-2">
          {item.assignedUserIds.map((userId, index) => {
            const user = fakeUsers.find(u => u.id === userId);
            return user ? (
              <React.Fragment key={index}>
                <img
                  className="mr-2 inline-block h-8 w-8 rounded-full ring-2 ring-white"
                  src={user.avatar}
                  alt={user.name}
                  width={32}
                  height={32}
                />
              </React.Fragment>
            ) : null;
          })}
        </div>
      </TableCell>
      <TableCell className="px-4 py-2">
        <div className="flex items-center justify-center space-x-2">
          <PlusCircleIcon className="h-6 w-6 cursor-pointer text-gray-500" onClick={onAddSubtask} />
          <BellIcon className="h-6 w-6 cursor-pointer text-gray-500" onClick={onSetReminder} />
          <PencilIcon className="h-6 w-6 cursor-pointer text-gray-500" onClick={onEditEvent} />
          <TrashIcon className="h-6 w-6 cursor-pointer text-gray-500" onClick={onDeleteEvent} />
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <Table className="min-w-full bg-white">
      <TableHeader>
        <TableRow>
          <TableHead className="w-1/6 px-4 py-2">Date</TableHead>
          <TableHead className="w-1/3 px-4 py-2">Title</TableHead>
          <TableHead className="w-1/3 px-4 py-2">Summary</TableHead>
          <TableHead className="w-1/6 px-4 py-2">People Assigned</TableHead>
          <TableHead className="w-1/6 px-4 py-2">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{eventsAndReminders.map(item => renderRow(item))}</TableBody>
    </Table>
  );
};

export default EventTable;
